import { useState, useRef, ChangeEvent, MouseEvent as ReactMouseEvent } from "react";
import styled from "styled-components";
import searchIcon from "../../assets/search.svg";

interface CampaignOption {
  id: string;
  name: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (value: string) => void;
  headerText?: string;
  placeholder?: string;
  errorText?: string;
  campaignNames?: CampaignOption[];
  fallbackCampaignNames?: CampaignOption[];
}

const SearchModal = ({
  isOpen,
  onClose,
  onApply,
  headerText,
  placeholder,
  errorText = "Please enter value",
  campaignNames,
  fallbackCampaignNames,
}: SearchModalProps) => {
  const [inputValue, setInputValue] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [isCampaignDropdownOpen, setIsCampaignDropdownOpen] = useState(false);
  const [error, setError] = useState("");
  const campaignInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const hasCampaignNames = Array.isArray(campaignNames);

  const filterCampaigns = (list?: CampaignOption[]) => {
    if (!Array.isArray(list)) return [];
    const query = campaignSearch.trim().toLowerCase();
    return query
      ? list.filter((c) => c.name.toLowerCase().includes(query))
      : list;
  };

  // Prefer /campaigns; if that list is empty or has no match, use fallback.
  const primaryCampaigns = filterCampaigns(campaignNames);
  const filteredCampaigns =
    hasCampaignNames && isCampaignDropdownOpen
      ? primaryCampaigns.length > 0
        ? primaryCampaigns
        : filterCampaigns(fallbackCampaignNames)
      : [];

  const handleApply = () => {
    if (hasCampaignNames) {
      const trimmedQueryId = inputValue.trim();
      if (!trimmedQueryId && !selectedCampaign) {
        setError(errorText);
        return;
      }
      setError("");
      onApply(trimmedQueryId || selectedCampaign);
    } else {
      if (!inputValue.trim()) {
        setError(errorText);
        return;
      }
      setError("");
      onApply(inputValue.trim());
    }
    onClose();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
    // Clear campaign selection when user starts typing a Query ID
    if (value.trim()) {
      setCampaignSearch("");
      setSelectedCampaign("");
      setIsCampaignDropdownOpen(false);
    }
    if (error) setError("");
  };

  const handleCampaignSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setCampaignSearch(val);
    setSelectedCampaign("");
    setIsCampaignDropdownOpen(true);
    // Clear Query ID when user starts typing a cohort name
    if (val.trim()) setInputValue("");
    if (error) setError("");
  };

  const handleSelectCampaign = (name: string) => {
    setSelectedCampaign(name);
    setCampaignSearch(name);
    setIsCampaignDropdownOpen(false);
    if (error) setError("");
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContent onClick={(e: ReactMouseEvent) => e.stopPropagation()}>
        <h3>{headerText}</h3>

        {hasCampaignNames ? (
          <>
            <FieldRow $disabled={!!(campaignSearch.trim() || selectedCampaign)}>
              <SectionLabel>Query ID</SectionLabel>
              <InputContainer $disabled={!!(campaignSearch.trim() || selectedCampaign)}>
                <img
                  src={searchIcon}
                  alt="Search"
                  style={{
                    filter:
                      "invert(58%) sepia(7%) saturate(154%) hue-rotate(178deg) brightness(95%) contrast(89%)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Enter Query ID"
                  value={inputValue}
                  onChange={handleChange}
                  disabled={!!(campaignSearch.trim() || selectedCampaign)}
                />
              </InputContainer>
            </FieldRow>

            <OrDivider>— or —</OrDivider>

            <FieldRow $disabled={!!inputValue.trim()}>
              <SectionLabel>Cohort Name</SectionLabel>
              <CampaignInputWrapper>
                <CampaignInputRow $disabled={!!inputValue.trim()}>
                  <img
                    src={searchIcon}
                    alt="Search"
                    style={{
                      filter:
                        "invert(58%) sepia(7%) saturate(154%) hue-rotate(178deg) brightness(95%) contrast(89%)",
                      width: 16,
                      height: 16,
                      marginRight: 8,
                      flexShrink: 0,
                    }}
                  />
                  <input
                    ref={campaignInputRef}
                    type="text"
                    placeholder="Search cohort name..."
                    value={campaignSearch}
                    onChange={handleCampaignSearchChange}
                    onFocus={() => !inputValue.trim() && setIsCampaignDropdownOpen(true)}
                    onBlur={() =>
                      setTimeout(() => setIsCampaignDropdownOpen(false), 150)
                    }
                    disabled={!!inputValue.trim()}
                  />
                </CampaignInputRow>
                {isCampaignDropdownOpen && (
                  <CampaignDropdown>
                    {filteredCampaigns.length > 0 ? (
                      filteredCampaigns.map((c) => (
                        <CampaignDropdownItem
                          key={c.id || c.name}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectCampaign(c.name)}
                        >
                          {c.name}
                        </CampaignDropdownItem>
                      ))
                    ) : (
                      <CampaignDropdownEmpty>
                        {!campaignNames?.length && !fallbackCampaignNames?.length
                          ? "No cohorts available"
                          : "No matching cohorts"}
                      </CampaignDropdownEmpty>
                    )}
                  </CampaignDropdown>
                )}
              </CampaignInputWrapper>
            </FieldRow>
          </>
        ) : (
          <InputContainer>
            <img
              src={searchIcon}
              alt="Search"
              style={{
                filter:
                  "invert(58%) sepia(7%) saturate(154%) hue-rotate(178deg) brightness(95%) contrast(89%)",
              }}
            />
            <input
              type="text"
              placeholder={placeholder || "Enter value"}
              value={inputValue}
              onChange={handleChange}
            />
          </InputContainer>
        )}

        {error && (
          <ErrorText $spaced={hasCampaignNames}>{error}</ErrorText>
        )}
        <ButtonContainer>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SearchButton onClick={handleApply}>Search</SearchButton>
        </ButtonContainer>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default SearchModal;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  text-align: center;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`;

/* Wrapper that owns the 80% centering so child inputs fill 100% of it */
const FieldRow = styled.div<{ $disabled?: boolean }>`
  width: 80%;
  margin: 0 auto;
  text-align: left;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  transition: opacity 0.15s ease;
`;

const InputContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${({ $disabled }) => ($disabled ? "#e0e0e0" : "#ccc")};
  border-radius: 4px;
  padding: 8px;
  margin: 6px 0 12px;
  width: 100%;
  box-sizing: border-box;
  background: ${({ $disabled }) => ($disabled ? "#f5f5f5" : "white")};

  img {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  input {
    border: none;
    outline: none;
    font-size: 14px;
    width: 100%;
    background: transparent;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "text")};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 15px;
`;

const CancelButton = styled.button`
  background: white;
  color: var(--Primary-Color-Shade-1);
  border: 1px solid var(--Primary-Color-Shade-1);
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
`;

const SearchButton = styled.button`
  background: var(--Primary-Color-Shade-1);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ErrorText = styled.div<{ $spaced?: boolean }>`
  color: #d32f2f;
  font-size: 12px;
  margin-top: ${({ $spaced }) => ($spaced ? "8px" : "-8px")};
  margin-bottom: 8px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 2px;
`;

const OrDivider = styled.div`
  font-size: 12px;
  color: #999;
  margin: 6px 0;
`;

const CampaignInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CampaignInputRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${({ $disabled }) => ($disabled ? "#e0e0e0" : "#ccc")};
  border-radius: 4px;
  padding: 8px;
  margin: 6px 0 0;
  box-sizing: border-box;
  background: ${({ $disabled }) => ($disabled ? "#f5f5f5" : "white")};

  input {
    border: none;
    outline: none;
    font-size: 14px;
    width: 100%;
    background: transparent;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "text")};
  }
`;

const CampaignDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 160px;
  overflow-y: auto;
  background: white;
  border: 1px solid #b7bec6;
  border-radius: 4px;
  z-index: 10;
  margin-top: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--Primary-Color-Shade-1);
    border-radius: 4px;
  }
`;

const CampaignDropdownItem = styled.div`
  padding: var(--space-sm, 8px) var(--space-md, 12px);
  cursor: pointer;
  font-size: var(--font-md, 13px);
  color: #333;
  font-weight: normal;
  text-transform: none;
  border-bottom: 1px solid #eee;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--Primary-Color-Shade-1-pale, #f0f4ff);
  }
`;

const CampaignDropdownEmpty = styled.div`
  padding: 10px 12px;
  font-size: 13px;
  color: #999;
  text-align: left;
`;
